/* eslint-disable vars-on-top */
import moment from 'moment';
import { hexToRgba } from '../helpers/globalFunctions';
import type { Action } from '../typings/reducers';
import { getTranslationKey } from '../modules/utils/helpers';

const brandPrimary = '#066cd2';
const brandSuccess = '#4dbd74';
const brandInfo = '#63c2de';
const brandWarning = '#ffc107';
const brandDanger = '#f86c6b';

type StatisticsState = { loadingDashboardStatistics: boolean } & Record<string, unknown>;

export default function (
  state: StatisticsState = { loadingDashboardStatistics: true },
  action: Action
): StatisticsState {
  try {
    switch (action.type) {
      case 'fetch_admin_dashboard_statistics':
        const { payload } = action;
        const adminDashboardMainChartData: Record<string, any> = {};
        let adminDashboardMainChartOverAllStats = {};

        if (payload && payload.length > 0) {
          const adminDashboardMainChartDataLocal: {
            maxValue: number;
            labels: string[];
            datasets: Array<{
              label: string;
              backgroundColor: string;
              borderColor: string;
              pointHoverBackgroundColor: string;
              borderWidth: number;
              data: number[];
            }>;
          } = {
            // eslint-disable-line
            maxValue: 0,
            labels: [],
            datasets: [
              {
                label: getTranslationKey('adminDashboard.views'),
                backgroundColor: hexToRgba(brandInfo, 10),
                borderColor: brandInfo,
                pointHoverBackgroundColor: '#fff',
                borderWidth: 2,
                data: []
              },
              {
                label: getTranslationKey('adminDashboard.shares'),
                backgroundColor: 'transparent',
                borderColor: brandSuccess,
                pointHoverBackgroundColor: '#fff',
                borderWidth: 2,
                data: []
              },
              {
                label: getTranslationKey('adminDashboard.createdSites'),
                backgroundColor: 'transparent',
                borderColor: brandDanger,
                pointHoverBackgroundColor: '#fff',
                borderWidth: 2,
                data: []
              },
              {
                label: getTranslationKey('adminDashboard.imagesCount'),
                backgroundColor: 'transparent',
                borderColor: brandPrimary,
                pointHoverBackgroundColor: '#fff',
                borderWidth: 2,
                data: []
              },
              {
                label: getTranslationKey('adminDashboard.appInstalls'),
                backgroundColor: 'transparent',
                borderColor: brandWarning,
                pointHoverBackgroundColor: '#fff',
                borderWidth: 2,
                data: []
              }
            ]
          };

          payload.forEach((data: any, index: number) => {
            const _data = { ...data };

            if (index === 0) {
              adminDashboardMainChartOverAllStats = _data; // eslint-disable-line
            } else {
              adminDashboardMainChartDataLocal.datasets[0].data.push(_data.views);
              if (_data.views > adminDashboardMainChartDataLocal.maxValue) {
                adminDashboardMainChartDataLocal.maxValue = _data.views;
              }

              adminDashboardMainChartDataLocal.datasets[1].data.push(_data.shares);
              if (_data.shares > adminDashboardMainChartDataLocal.maxValue) {
                adminDashboardMainChartDataLocal.maxValue = _data.shares;
              }

              adminDashboardMainChartDataLocal.datasets[2].data.push(_data.createdSites);
              if (_data.createdSites > adminDashboardMainChartDataLocal.maxValue) {
                adminDashboardMainChartDataLocal.maxValue = _data.createdSites;
              }

              adminDashboardMainChartDataLocal.datasets[3].data.push(_data.uploadedImages);
              if (_data.uploadedImages > adminDashboardMainChartDataLocal.maxValue) {
                adminDashboardMainChartDataLocal.maxValue = _data.uploadedImages;
              }

              adminDashboardMainChartDataLocal.datasets[4].data.push(_data.appInstalls);
              if (_data.appInstalls > adminDashboardMainChartDataLocal.maxValue) {
                adminDashboardMainChartDataLocal.maxValue = _data.appInstalls;
              }

              const date = moment(_data.date).format('MM-DD');
              adminDashboardMainChartDataLocal.labels.push(date);
            }
          });

          adminDashboardMainChartData.datasets = adminDashboardMainChartDataLocal.datasets;
          adminDashboardMainChartData.labels = adminDashboardMainChartDataLocal.labels;
          adminDashboardMainChartData.maxValue = adminDashboardMainChartDataLocal.maxValue;
        }

        return {
          ...state,
          adminDashboardMainChartData,
          adminDashboardMainChartOverAllStats
        };
      case 'fetch_user_dashboard_statistics':
        const _userDashboardMainChartData = {}; // eslint-disable-line @typescript-eslint/no-unused-vars
        let userDashboardMainChartOverAllStats = {};

        if (action.payload && action.payload.length > 0) {
          const userDashboardMainChartDataLocal: {
            maxValue: number;
            labels: string[];
            datasets: Array<{
              label: string;
              backgroundColor: string;
              borderColor: string;
              pointHoverBackgroundColor: string;
              borderWidth: number;
              data: number[];
            }>;
          } = {
            maxValue: 0,
            labels: [],
            datasets: [
              {
                label: getTranslationKey('userDashboard.views'),
                backgroundColor: hexToRgba(brandInfo, 10),
                borderColor: brandInfo,
                pointHoverBackgroundColor: '#fff',
                borderWidth: 2,
                data: []
              },
              {
                label: getTranslationKey('userDashboard.shares'),
                backgroundColor: 'transparent',
                borderColor: brandSuccess,
                pointHoverBackgroundColor: '#fff',
                borderWidth: 2,
                data: []
              },
              {
                label: getTranslationKey('userDashboard.appInstalls'),
                backgroundColor: 'transparent',
                borderColor: brandDanger,
                pointHoverBackgroundColor: '#fff',
                borderWidth: 2,
                data: []
              },
              {
                label: getTranslationKey('userDashboard.downloads'),
                backgroundColor: 'transparent',
                borderColor: brandWarning,
                pointHoverBackgroundColor: '#fff',
                borderWidth: 2,
                data: []
              }
            ]
          };

          action.payload.forEach((data: any, index: number) => {
            const _data = { ...data };

            if (moment(_data.date).isSame('2020-08-05', 'day')) return;

            if (index === 0) {
              userDashboardMainChartOverAllStats = _data; // eslint-disable-line
            } else {
              userDashboardMainChartDataLocal.datasets[0].data.push(_data.views);
              if (_data.views > userDashboardMainChartDataLocal.maxValue) {
                userDashboardMainChartDataLocal.maxValue = _data.views;
              }

              userDashboardMainChartDataLocal.datasets[1].data.push(_data.shares);
              if (_data.shares > userDashboardMainChartDataLocal.maxValue) {
                userDashboardMainChartDataLocal.maxValue = _data.shares;
              }

              userDashboardMainChartDataLocal.datasets[2].data.push(_data.appInstalls);
              if (_data.appInstalls > userDashboardMainChartDataLocal.maxValue) {
                userDashboardMainChartDataLocal.maxValue = _data.appInstalls;
              }

              userDashboardMainChartDataLocal.datasets[3].data.push(_data.downloads);
              if (_data.downloads > userDashboardMainChartDataLocal.maxValue) {
                userDashboardMainChartDataLocal.maxValue = _data.downloads;
              }

              const date = moment(_data.date).format('MM-DD');
              userDashboardMainChartDataLocal.labels.push(date);
            }
          });

          return {
            ...state,
            loadingDashboardStatistics: false,
            userDashboardMainChartData: userDashboardMainChartDataLocal, // eslint-disable-line
            userDashboardMainChartOverAllStats // eslint-disable-line
          };
        }

        return {
          ...state,
          loadingDashboardStatistics: false
        };
      default:
        return state;
    }
  } catch (e) {
    console.log(e);
  }
}
